import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, Mail, Lock, AlertCircle } from 'lucide-react';

// FURD Tech Logo - The app/product brand (separate from in-app company branding)
const FURD_TECH_LOGO = '/furd-tech-logo.png';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* Mobile Hero Section - Dark branded header with glow */}
      <div className="lg:hidden bg-gradient-to-b from-[#0a1628] via-[#0d1f35] to-[#1e3a5f] relative overflow-hidden px-6 pt-16 pb-12">
        {/* Animated glow effects */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-80 bg-emerald-500/15 blur-[100px] rounded-full" />
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-56 h-56 bg-emerald-400/20 blur-[60px] rounded-full animate-pulse" />
        <div className="absolute top-16 left-1/2 -translate-x-1/2 w-32 h-32 bg-emerald-300/25 blur-[40px] rounded-full" />
        
        {/* Subtle grid pattern overlay */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} />
        
        <div className="relative z-10 text-center">
          {/* FURD Tech Logo with enhanced glow */}
          <div className="relative mx-auto mb-6 w-fit">
            <div className="absolute inset-0 bg-emerald-400/30 blur-3xl rounded-full scale-[2]" />
            <div className="absolute inset-0 bg-emerald-500/20 blur-2xl rounded-full scale-150" />
            <img 
              src={FURD_TECH_LOGO} 
              alt="FURD Tech" 
              className="h-20 w-auto mx-auto relative z-10 drop-shadow-2xl"
            />
          </div>
          
          <p className="text-white/70 text-lg font-light tracking-wide">
            MEP Consultancy OS
          </p>
        </div>
      </div>

      {/* Mobile Form Section - Clean white card */}
      <div className="lg:hidden flex-1 bg-white px-6 py-8 -mt-4 rounded-t-3xl relative z-20 shadow-2xl">
        <div className="max-w-sm mx-auto">
          <h1 className="font-heading text-2xl font-bold text-[#1e3a5f] mb-2">
            Welcome back
          </h1>
          <p className="text-slate-500 text-sm mb-6">
            Sign in to your account
          </p>
          
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl mb-5 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email-mobile" className="text-slate-700 font-medium text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email-mobile"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="pl-12 h-12 rounded-xl bg-slate-50 border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f] text-base"
                  data-testid="login-email-input"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password-mobile" className="text-slate-700 font-medium text-sm">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password-mobile"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-12 h-12 rounded-xl bg-slate-50 border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f] text-base"
                  data-testid="login-password-input"
                  required
                />
              </div>
            </div>
            
            <div className="flex items-center justify-end">
              <Link to="/forgot-password" className="text-sm text-[#1e3a5f] font-medium hover:underline">
                Forgot password?
              </Link>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl font-semibold transition-all duration-200 shadow-lg shadow-[#1e3a5f]/20"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign in'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </form>
          
          <p className="mt-6 text-center text-slate-500 text-sm">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-[#1e3a5f] font-semibold hover:underline">
              Create account
            </Link>
          </p>
        </div>
      </div>

      {/* Desktop Left - Form */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          <div className="flex items-center gap-3 mb-12">
            <img 
              src={FURD_TECH_LOGO} 
              alt="FURD Tech" 
              className="h-10 w-auto"
            />
            <span className="font-heading text-2xl font-bold text-[#1e3a5f]">OpsFlow</span>
          </div>
          
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-3">
            Welcome back
          </h1>
          <p className="text-slate-500 text-lg mb-10">
            Sign in to manage your MEP consultancy operations
          </p>
          
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="pl-12 h-12 rounded-xl bg-slate-50 border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                  data-testid="login-email-input-desktop"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-12 h-12 rounded-xl bg-slate-50 border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                  data-testid="login-password-input-desktop"
                  required
                />
              </div>
            </div>
            
            <div className="flex items-center justify-end">
              <Link to="/forgot-password" className="text-sm text-slate-500 hover:text-[#1e3a5f]">
                Forgot password?
              </Link>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl font-semibold transition-all duration-200"
              data-testid="login-submit-btn-desktop"
            >
              {loading ? 'Signing in...' : 'Sign in'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </form>
          
          <p className="mt-8 text-center text-slate-500">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-[#1e3a5f] font-semibold hover:underline">
              Create account
            </Link>
          </p>
        </div>
      </div>
      
      {/* Desktop Right - Hero Image */}
      <div className="hidden lg:flex flex-1 bg-[#000000] items-center justify-center p-16 relative overflow-hidden">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/50 via-transparent to-slate-900/30" />
        
        <div className="relative z-10 text-center max-w-lg">
          {/* FURD Tech Logo with green glow */}
          <div className="relative mx-auto mb-8">
            {/* Green glow effect behind the logo */}
            <div className="absolute inset-0 bg-emerald-500/20 blur-3xl rounded-full scale-150 animate-pulse" />
            <div className="absolute inset-0 bg-emerald-400/10 blur-2xl rounded-full scale-125" />
            <img 
              src={FURD_TECH_LOGO} 
              alt="FURD Tech" 
              className="h-24 md:h-32 w-auto mx-auto relative z-10"
            />
          </div>
          
          <p className="text-white/60 text-xl mb-10 font-light">
            MEP Consultancy OS
          </p>
          
          {/* Feature list with better styling */}
          <div className="space-y-4 text-left max-w-sm mx-auto bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            {[
              { text: 'Enquiry Management & Decision Engine', icon: '📋' },
              { text: 'Quote Generation with Approval Workflow', icon: '💼' },
              { text: 'Pipeline CRM & Outreach Tracking', icon: '📊' },
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3 text-white/80 group">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-sm group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <span className="text-sm font-medium">{feature.text}</span>
              </div>
            ))}
          </div>
          
          <div className="mt-12 pt-8 border-t border-white/10">
            <p className="text-white/30 text-xs uppercase tracking-widest font-medium">
              Commercial Office Fit-Outs • CAT A & CAT B • Residential
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(true);
  const [requiresSetup, setRequiresSetup] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: ''
  });

  useEffect(() => {
    // Check if this is first-time setup
    const checkSetup = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/setup-status`);
        const data = await res.json();
        setRequiresSetup(data.requires_setup);
      } catch (err) {
        console.error('Setup check failed:', err);
      } finally {
        setLoading(false);
      }
    };
    checkSetup();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    
    try {
      await register(formData);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }

  // First-time setup - allow registration
  if (requiresSetup) {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row" data-testid="register-page">
        {/* Mobile Hero Section */}
        <div className="lg:hidden bg-gradient-to-b from-[#0a1628] via-[#0d1f35] to-[#1e3a5f] relative overflow-hidden px-6 pt-12 pb-8">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-80 bg-emerald-500/15 blur-[100px] rounded-full" />
          <div className="absolute top-10 left-1/2 -translate-x-1/2 w-56 h-56 bg-emerald-400/20 blur-[60px] rounded-full animate-pulse" />
          
          <div className="relative z-10 text-center">
            <div className="relative mx-auto mb-4 w-fit">
              <div className="absolute inset-0 bg-emerald-400/30 blur-3xl rounded-full scale-[2]" />
              <img src={FURD_TECH_LOGO} alt="FURD Tech" className="h-16 w-auto mx-auto relative z-10 drop-shadow-2xl" />
            </div>
            <p className="text-white/70 text-base font-light">MEP Consultancy OS</p>
          </div>
        </div>

        {/* Mobile Form Section */}
        <div className="lg:hidden flex-1 bg-white px-6 py-6 -mt-4 rounded-t-3xl relative z-20 shadow-2xl">
          <div className="max-w-sm mx-auto">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-5">
              <h3 className="font-medium text-emerald-800 text-sm mb-1">🎉 Welcome to OpsFlow!</h3>
              <p className="text-emerald-700 text-xs">Create your admin account. You&apos;ll be set as the Director.</p>
            </div>
            
            <h1 className="font-heading text-xl font-bold text-[#1e3a5f] mb-1">Create Admin Account</h1>
            <p className="text-slate-500 text-sm mb-5">Set up your workspace</p>
            
            {error && (
              <div className="bg-red-50 text-red-700 px-3 py-2 rounded-xl mb-4 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-slate-700 font-medium text-sm">First name</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    placeholder="Tim"
                    className="h-11 rounded-xl bg-slate-50 border-slate-200 text-base"
                    data-testid="register-firstname-input"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-700 font-medium text-sm">Last name</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    placeholder="Ayoola"
                    className="h-11 rounded-xl bg-slate-50 border-slate-200 text-base"
                    data-testid="register-lastname-input"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-slate-700 font-medium text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="you@furd.co.uk"
                    className="pl-12 h-11 rounded-xl bg-slate-50 border-slate-200 text-base"
                    data-testid="register-email-input"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-slate-700 font-medium text-sm">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Min. 8 characters"
                    className="pl-12 h-11 rounded-xl bg-slate-50 border-slate-200 text-base"
                    data-testid="register-password-input"
                    required
                    minLength={8}
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl font-semibold shadow-lg shadow-[#1e3a5f]/20"
                data-testid="register-submit-btn"
              >
                {submitting ? 'Creating...' : 'Create Account'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>
            
            <p className="mt-5 text-center text-slate-500 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-[#1e3a5f] font-semibold hover:underline">Sign in</Link>
            </p>
          </div>
        </div>

        {/* Desktop Form */}
        <div className="hidden lg:flex flex-1 items-center justify-center p-8 md:p-16 bg-white">
          <div className="w-full max-w-md animate-fade-in">
            <div className="flex items-center gap-3 mb-8">
              <img src={FURD_TECH_LOGO} alt="FURD Tech" className="h-10 w-auto" />
            </div>
            
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-8">
              <h3 className="font-medium text-emerald-800 mb-1">🎉 Welcome to OpsFlow!</h3>
              <p className="text-emerald-700 text-sm">
                Create your admin account to get started. You&apos;ll be set as the Director.
              </p>
            </div>
            
            <h1 className="font-heading text-3xl font-bold text-[#1e3a5f] mb-2">Create Admin Account</h1>
            <p className="text-slate-500 mb-8">Set up your workspace as the Director</p>
            
            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">First name</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    placeholder="Tim"
                    className="h-12 rounded-xl bg-slate-50 border-slate-200"
                    data-testid="register-firstname-input-desktop"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Last name</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    placeholder="Ayoola"
                    className="h-12 rounded-xl bg-slate-50 border-slate-200"
                    data-testid="register-lastname-input-desktop"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="you@furd.co.uk"
                    className="pl-12 h-12 rounded-xl bg-slate-50 border-slate-200"
                    data-testid="register-email-input-desktop"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Min. 8 characters"
                    className="pl-12 h-12 rounded-xl bg-slate-50 border-slate-200"
                    data-testid="register-password-input-desktop"
                    required
                    minLength={8}
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl font-semibold"
                data-testid="register-submit-btn-desktop"
              >
                {submitting ? 'Creating account...' : 'Create Director Account'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>
          </div>
        </div>
        
        {/* Desktop Hero */}
        <div className="hidden lg:flex flex-1 bg-[#000000] items-center justify-center p-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/50 via-transparent to-slate-900/30" />
          <div className="relative z-10 text-center max-w-lg">
            <div className="relative mx-auto mb-8">
              <div className="absolute inset-0 bg-emerald-500/20 blur-3xl rounded-full scale-150 animate-pulse" />
              <div className="absolute inset-0 bg-emerald-400/10 blur-2xl rounded-full scale-125" />
              <img src={FURD_TECH_LOGO} alt="FURD Tech" className="h-24 md:h-32 w-auto mx-auto relative z-10" />
            </div>
            <p className="text-white/60 text-xl mb-10 font-light">MEP Consultancy OS</p>
            <div className="space-y-4 text-left max-w-sm mx-auto bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              {[
                { text: 'Enquiry Management & Decision Engine', icon: '📋' },
                { text: 'Quote Generation with Approval Workflow', icon: '💼' },
                { text: 'Pipeline CRM & Outreach Tracking', icon: '📊' },
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-3 text-white/80 group">
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-sm">{feature.icon}</div>
                  <span className="text-sm font-medium">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // System already has users - show private workspace message
  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="register-page">
      {/* Mobile Hero Section */}
      <div className="lg:hidden bg-gradient-to-b from-[#0a1628] via-[#0d1f35] to-[#1e3a5f] relative overflow-hidden px-6 pt-12 pb-8">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-80 bg-amber-500/15 blur-[100px] rounded-full" />
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-56 h-56 bg-amber-400/20 blur-[60px] rounded-full" />
        
        <div className="relative z-10 text-center">
          <div className="relative mx-auto mb-4 w-fit">
            <div className="absolute inset-0 bg-amber-400/30 blur-3xl rounded-full scale-[2]" />
            <img src={FURD_TECH_LOGO} alt="FURD Tech" className="h-16 w-auto mx-auto relative z-10 drop-shadow-2xl" />
          </div>
          <p className="text-white/70 text-base font-light">MEP Consultancy OS</p>
        </div>
      </div>

      {/* Mobile Content */}
      <div className="lg:hidden flex-1 bg-white px-6 py-6 -mt-4 rounded-t-3xl relative z-20 shadow-2xl">
        <div className="max-w-sm mx-auto text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-amber-600" />
          </div>
          
          <h1 className="font-heading text-xl font-bold text-[#1e3a5f] mb-2">Private Workspace</h1>
          <p className="text-slate-500 text-sm mb-5">Request an invite from your Director to join.</p>
          
          <div className="bg-slate-50 rounded-xl p-4 text-left mb-5">
            <h3 className="font-medium text-slate-900 text-sm mb-2">How to get access:</h3>
            <ol className="text-slate-600 text-xs space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">1</span>
                <span>Contact your Director or team admin</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">2</span>
                <span>They will send you an invite email</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-4 h-4 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">3</span>
                <span>Click the link to create your account</span>
              </li>
            </ol>
          </div>
          
          <p className="text-slate-500 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-[#1e3a5f] font-semibold hover:underline">Sign in</Link>
          </p>
        </div>
      </div>

      {/* Desktop Content */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-8 md:p-16 bg-white">
        <div className="w-full max-w-md animate-fade-in text-center">
          <div className="flex items-center gap-3 justify-center mb-12">
            <img src={FURD_TECH_LOGO} alt="FURD Tech" className="h-10 w-auto" />
            <span className="font-heading text-2xl font-bold text-[#1e3a5f]">OpsFlow</span>
          </div>
          
          <div className="w-20 h-20 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-amber-600" />
          </div>
          
          <h1 className="font-heading text-3xl font-bold text-[#1e3a5f] mb-3">Private Workspace</h1>
          <p className="text-slate-500 text-lg mb-8">
            This workspace is private. Please request an invite from your Director.
          </p>
          
          <div className="bg-slate-50 rounded-xl p-6 text-left mb-8">
            <h3 className="font-medium text-slate-900 mb-3">How to get access:</h3>
            <ol className="text-slate-600 text-sm space-y-2">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
                <span>Contact your Director or team administrator</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
                <span>They will send you an invite to your work email</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
                <span>Click the link in the email to create your account</span>
              </li>
            </ol>
          </div>
          
          <p className="text-slate-500">
            Already have an account?{' '}
            <Link to="/login" className="text-[#1e3a5f] font-semibold hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
      
      {/* Desktop Hero */}
      <div className="hidden lg:flex flex-1 bg-[#000000] items-center justify-center p-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/50 via-transparent to-slate-900/30" />
        <div className="relative z-10 text-center max-w-lg">
          <div className="relative mx-auto mb-8">
            <div className="absolute inset-0 bg-amber-500/20 blur-3xl rounded-full scale-150 animate-pulse" />
            <div className="absolute inset-0 bg-amber-400/10 blur-2xl rounded-full scale-125" />
            <img src={FURD_TECH_LOGO} alt="FURD Tech" className="h-24 md:h-32 w-auto mx-auto relative z-10" />
          </div>
          <h2 className="font-heading text-3xl font-bold text-white mb-4">Secure by Design</h2>
          <p className="text-white/60 text-lg">
            OpsFlow uses invite-only access to keep your team&apos;s data secure and private
          </p>
        </div>
      </div>
    </div>
  );
};
